import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Eye, BookOpen, CheckCircle } from 'lucide-react';

const StepCard = ({
  stepData,
  stepIndex,
  totalSteps,
  phaseColor,
  isLastStep,
  onNext,
  onBack,
  onComplete,
}) => {
  const [whyOpen, setWhyOpen] = useState(false);

  if (!stepData) return null;

  const isCelebration = stepData.stepNumber === totalSteps || stepData.type === 'conclusion' || isLastStep;
  const isFirst = stepIndex === 0;

  // Phase-aware accent colors
  const accent = phaseColor?.includes('pink') ? {
    border: 'border-pink-300', bg: 'bg-pink-50', text: 'text-pink-700',
    lightBg: 'from-pink-50 to-rose-50', dot: 'bg-pink-400',
  } : phaseColor?.includes('green') ? {
    border: 'border-teal-300', bg: 'bg-teal-50', text: 'text-teal-700',
    lightBg: 'from-green-50 to-teal-50', dot: 'bg-teal-400',
  } : phaseColor?.includes('blue') ? {
    border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700',
    lightBg: 'from-blue-50 to-indigo-50', dot: 'bg-blue-400',
  } : {
    border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700',
    lightBg: 'from-indigo-50 to-purple-50', dot: 'bg-purple-400',
  };

  // ---- CELEBRATION / COMPLETION LAYOUT ----
  if (isCelebration && (stepData.celebration?.length > 0 || stepData.reviewQuestions?.length > 0 || stepData.activitySteps?.length > 0 || stepData.nextSteps?.length > 0 || stepData.type === 'conclusion')) {
    return (
      <div className="space-y-4">

        {/* Celebration header */}
        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl shadow-md p-6 sm:p-8 text-center">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Activity Complete!</h2>
          <p className="text-gray-500 text-sm">Step {stepIndex + 1} of {totalSteps}</p>
        </div>

        {/* What you practiced — list of activity steps */}
        {stepData.activitySteps?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-green-200 overflow-hidden">
            <div className="bg-green-50 px-5 py-3">
              <h3 className="text-sm font-bold text-green-700 tracking-wide">What You Practiced</h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2.5">
                {stepData.activitySteps.map((step, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{step}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Reflect — review questions */}
        {stepData.reviewQuestions?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-amber-200 overflow-hidden">
            <div className="bg-amber-50 px-5 py-3">
              <h3 className="text-sm font-bold text-amber-700 tracking-wide">Reflect</h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2.5">
                {stepData.reviewQuestions.map((q, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="text-amber-500 font-bold mt-0.5">?</span>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{q}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Keep going — next steps */}
        {stepData.nextSteps?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-blue-200 overflow-hidden">
            <div className="bg-blue-50 px-5 py-3">
              <h3 className="text-sm font-bold text-blue-700 tracking-wide">Keep the Learning Going</h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2.5">
                {stepData.nextSteps.map((step, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-400 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{step}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Why this activity works */}
        {stepData.whyThisWorks && (
          <div className="bg-blue-50 rounded-xl p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-blue-700 mb-1">Why This Activity Works</h3>
                <p className="text-blue-700 text-sm leading-relaxed">{stepData.whyThisWorks}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 pt-2 pb-8">
          <button
            onClick={onBack}
            className="flex items-center justify-center gap-1 px-5 py-3.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <button
            onClick={onComplete}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3.5 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow"
          >
            Complete Activity
          </button>
        </div>
      </div>
    );
  }

  // ---- INTRO LAYOUT ----
  if (stepData.type === 'intro' && (stepData.materials || stepData.parentGuide || stepData.introTip)) {
    return (
      <div className="space-y-4">

        {/* Intro header */}
        <div className={`bg-gradient-to-br ${accent.lightBg} rounded-2xl shadow-md p-5 sm:p-6`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs font-bold ${accent.text} tracking-wider uppercase`}>
              Step {stepIndex + 1} of {totalSteps}
            </span>
            {stepData.duration && (
              <span className={`text-xs font-semibold ${accent.text} ${accent.bg} rounded-full px-3 py-1`}>
                {stepData.duration}
              </span>
            )}
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{stepData.name}</h2>
          {stepData.hook && (
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{stepData.hook}</p>
          )}
        </div>

        {/* Materials */}
        {stepData.materials && stepData.materials.length > 0 && (
          <div className={`bg-white rounded-2xl shadow-md border ${accent.border} overflow-hidden`}>
            <div className={`${accent.bg} px-5 py-3`}>
              <h3 className={`text-sm font-bold ${accent.text} tracking-wide`}>📦 What You Need</h3>
            </div>
            <div className="p-5">
              <div className="flex flex-wrap gap-2">
                {stepData.materials.map((item, i) => (
                  <span key={i} className={`inline-flex items-center gap-1.5 ${accent.bg} ${accent.text} font-semibold text-sm px-3 py-1.5 rounded-full`}>
                    ✓ {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* What you'll learn */}
        {stepData.parentGuide && stepData.parentGuide.length > 0 && (
          <div className={`bg-white rounded-2xl shadow-md border ${accent.border} overflow-hidden`}>
            <div className={`${accent.bg} px-5 py-3`}>
              <h3 className={`text-sm font-bold ${accent.text} tracking-wide`}>💡 What This Builds</h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2.5">
                {stepData.parentGuide.map((item, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className={`${accent.text} font-bold mt-0.5`}>•</span>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Parent tip */}
        {stepData.introTip && (
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-xl shadow-sm p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0">💡</span>
              <div>
                <h3 className="text-sm font-bold text-amber-700 mb-1">Parent Tip</h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{stepData.introTip}</p>
              </div>
            </div>
          </div>
        )}

        {/* Why this works */}
        {stepData.whyThisWorks && (
          <div className="bg-blue-50 rounded-xl overflow-hidden">
            <button
              onClick={() => setWhyOpen(!whyOpen)}
              className="flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:text-blue-800 transition-colors w-full"
            >
              <BookOpen className="w-4 h-4" />
              <span className="font-semibold">Why this works</span>
              <ChevronDown
                className={`w-4 h-4 ml-auto transition-transform duration-200 ${whyOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${whyOpen ? 'max-h-40 pb-3' : 'max-h-0'}`}>
              <p className="text-blue-700 text-xs sm:text-sm leading-relaxed px-4">{stepData.whyThisWorks}</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 pt-2 pb-8">
          <button
            onClick={onNext}
            className={`flex-1 bg-gradient-to-r ${phaseColor || 'from-purple-500 to-pink-500'} text-white py-3.5 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow`}
          >
            Next Step
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ---- REGULAR STEP LAYOUT ----
  return (
    <div className="space-y-4">

      {/* ---- HEADER CARD ---- */}
      <div className={`bg-gradient-to-br ${accent.lightBg} rounded-2xl shadow-md p-5 sm:p-6`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-xs font-bold ${accent.text} tracking-wider uppercase`}>
            Step {stepIndex + 1} of {totalSteps}
          </span>
          {stepData.duration && (
            <span className={`text-xs font-semibold ${accent.text} ${accent.bg} rounded-full px-3 py-1`}>
              {stepData.duration}
            </span>
          )}
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {stepData.name}
        </h2>
        {stepData.hook && (
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            {stepData.hook}
          </p>
        )}
      </div>

      {/* ---- LESSON PLAN (Say + Do paired by moment) ---- */}
      {(stepData.sayThis?.length > 0 || stepData.thenDoThis?.length > 0) && (() => {
        const sayItems = stepData.sayThis || [];
        const doItems = stepData.thenDoThis || [];
        const hasBoth = sayItems.length > 0 && doItems.length > 0;

        // Each Say anchors one moment; extra Dos pile into the last moment
        const moments = sayItems.length === 0
          ? [{ say: null, dos: doItems }]
          : sayItems.map((say, i) => {
              const isLast = i === sayItems.length - 1;
              const dos = isLast ? doItems.slice(i) : (doItems[i] ? [doItems[i]] : []);
              return { say, dos };
            });

        return (
        <div className={`bg-white rounded-2xl shadow-md border ${accent.border} overflow-hidden`}>
          <div className={`${accent.bg} px-5 py-3`}>
            <h3 className={`text-sm font-bold ${accent.text} tracking-wide`}>
              📋 Activity Plan
            </h3>
          </div>

          <div className="p-5 space-y-0">
            {moments.map((moment, i) => {
              const isHelper = moment.say?.startsWith('(');
              const showDoLabel = hasBoth && moment.dos.length > 0;
              return (
                <div key={i} className={`flex gap-3 py-4${i < moments.length - 1 ? ` border-b ${accent.border} border-opacity-30` : ''}`}>
                  {/* Moment number */}
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full ${accent.dot} text-white text-xs font-bold flex items-center justify-center mt-0.5`}>
                    {i + 1}
                  </span>

                  {/* Say + Do content */}
                  <div className="flex-1 space-y-2">
                    {moment.say && (
                      isHelper ? (
                        <p className="text-xs text-gray-400 italic">{moment.say}</p>
                      ) : (
                        <div>
                          {hasBoth && (
                            <p className={`text-xs font-bold ${accent.text} tracking-wider uppercase mb-1.5`}>🗣️ Say</p>
                          )}
                          <div className={`border-l-3 ${accent.border} ${accent.bg} rounded-r-xl px-4 py-2.5`} style={{ borderLeftWidth: '3px' }}>
                            <p className="text-gray-800 text-sm sm:text-base leading-relaxed">{moment.say}</p>
                          </div>
                        </div>
                      )
                    )}
                    {moment.dos.length > 0 && (
                      <div className="space-y-1.5">
                        {showDoLabel && (
                          <p className={`text-xs font-bold ${accent.text} tracking-wider uppercase mb-1`}>✋ Do</p>
                        )}
                        {moment.dos.length === 1 ? (
                          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{moment.dos[0]}</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {moment.dos.map((action, j) => (
                              <li key={j} className="flex gap-2 items-start">
                                <span className="mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400" />
                                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{action}</p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* ---- WATCH FOR ---- */}
      {stepData.watchFor && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-xl shadow-sm p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-amber-700 mb-1">Watch For</h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                {stepData.watchFor}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ---- WHY THIS WORKS (collapsed) ---- */}
      {stepData.whyThisWorks && (
        <div className="bg-blue-50 rounded-xl overflow-hidden">
          <button
            onClick={() => setWhyOpen(!whyOpen)}
            className="flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:text-blue-800 transition-colors w-full"
          >
            <BookOpen className="w-4 h-4" />
            <span className="font-semibold">Why this works</span>
            <ChevronDown
              className={`w-4 h-4 ml-auto transition-transform duration-200 ${whyOpen ? 'rotate-180' : ''}`}
            />
          </button>
          <div className={`overflow-hidden transition-all duration-200 ${whyOpen ? 'max-h-40 pb-3' : 'max-h-0'}`}>
            <p className="text-blue-700 text-xs sm:text-sm leading-relaxed px-4">
              {stepData.whyThisWorks}
            </p>
          </div>
        </div>
      )}

      {/* ---- NAVIGATION ---- */}
      <div className="flex gap-3 pt-2 pb-8">
        {!isFirst && (
          <button
            onClick={onBack}
            className="flex items-center justify-center gap-1 px-5 py-3.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        )}
        {isLastStep ? (
          <button
            onClick={onComplete}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3.5 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow"
          >
            Complete Activity
          </button>
        ) : (
          <button
            onClick={onNext}
            className={`flex-1 bg-gradient-to-r ${phaseColor || 'from-purple-500 to-pink-500'} text-white py-3.5 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow`}
          >
            Next Step
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StepCard;
